import React, { useState, useEffect, useCallback } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, RadialBarChart, RadialBar, Legend,
} from 'recharts';
import {
  TrendingUp, Users, DollarSign, FileText, AlertTriangle,
  Brain, Loader2, ChevronRight, Zap,
} from 'lucide-react';
import { analyticsApi } from '../../utils/api';
import { formatMontant } from '../../utils/calculs';

const C_PTISLU = '#1e2d4a';
const C_CHOUBIDOUX = '#c0985a';
const TABS = [
  { id: 'global', label: 'Vue Globale' },
  { id: 'ia', label: '🧠 Optimisations IA' },
  { id: 'detail', label: '📊 Détail Financier' },
];

/* ── Skeleton ── */
function Skeleton({ className = '' }) {
  return <div className={`animate-pulse bg-gray-200 rounded ${className}`} />;
}

/* ── KPI Card ── */
function KpiCard({ label, value, sub, colorClass, icon: Icon, loading }) {
  return (
    <div className="card p-5 flex-1 min-w-0">
      {loading ? (
        <><Skeleton className="h-7 w-24 mb-2" /><Skeleton className="h-4 w-32" /></>
      ) : (
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className={`text-2xl font-bold truncate ${colorClass}`}>{value}</div>
            <div className="text-sm text-gray-500 mt-1 leading-tight">{label}</div>
            {sub && <div className="text-xs text-gray-400 mt-0.5">{sub}</div>}
          </div>
          {Icon && <Icon size={22} className={`${colorClass} opacity-50 flex-shrink-0 mt-0.5`} />}
        </div>
      )}
    </div>
  );
}

/* ── Structure Column ── */
function StructureCol({ data, color, loading }) {
  if (loading) return (
    <div className="card p-5 flex-1">
      <Skeleton className="h-5 w-32 mb-4" />
      {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-4 w-full mb-3" />)}
    </div>
  );
  if (!data) return null;
  const occ = data.capacite > 0 ? Math.round((data.enfants_actifs / data.capacite) * 100) : 0;
  return (
    <div className="card p-5 flex-1">
      <div className="flex items-center gap-2 mb-4">
        <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
        <span className="font-bold text-gray-800 text-base">{data.nom}</span>
      </div>
      <div className="mb-4">
        <div className="flex justify-between text-sm mb-1">
          <span className="text-gray-500">Enfants actifs</span>
          <span className="font-semibold">{data.enfants_actifs} / {data.capacite}</span>
        </div>
        <div className="w-full bg-gray-100 rounded-full h-2.5">
          <div
            className="h-2.5 rounded-full transition-all"
            style={{ width: `${Math.min(occ, 100)}%`, backgroundColor: color }}
          />
        </div>
      </div>
      <div className="text-3xl font-black mb-1" style={{ color }}>{occ}%</div>
      <div className="text-xs text-gray-400 mb-4">Taux d'occupation</div>
      <div className="space-y-2 border-t pt-3">
        <div className="flex justify-between text-sm">
          <span className="text-gray-500">CA encaissé ce mois</span>
          <span className="font-medium text-green-600">{formatMontant(data.ca_encaisse_mois)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-500">CA facturé ce mois</span>
          <span className="font-medium">{formatMontant(data.ca_facture_mois)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-500">Factures en retard</span>
          <span className={`font-semibold ${data.factures_retard > 0 ? 'text-red-600' : 'text-gray-400'}`}>
            {data.factures_retard > 0 ? `⚠ ${data.factures_retard}` : '—'}
          </span>
        </div>
      </div>
    </div>
  );
}

/* ── Radial Gauge ── */
function OccupationGauge({ nom, pct, color }) {
  const data = [{ name: nom, value: pct, fill: color }];
  return (
    <div className="flex flex-col items-center">
      <div className="text-sm font-medium text-gray-600 mb-1">{nom}</div>
      <RadialBarChart width={160} height={160} cx={80} cy={80} innerRadius={50} outerRadius={70}
        barSize={14} data={data} startAngle={90} endAngle={-270}>
        <RadialBar minAngle={5} background clockWise dataKey="value" />
      </RadialBarChart>
      <div className="text-2xl font-black -mt-6" style={{ color }}>{pct}%</div>
    </div>
  );
}

/* ── Custom Tooltip for CA chart ── */
function CaTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const total = payload.reduce((s, p) => s + (p.value || 0), 0);
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm">
      <div className="font-semibold text-gray-700 mb-1">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: p.color }} />
          <span className="text-gray-600">{p.name}:</span>
          <span className="font-medium">{formatMontant(p.value)}</span>
        </div>
      ))}
      <div className="border-t mt-1 pt-1 text-gray-700 font-semibold">
        Total: {formatMontant(total)}
      </div>
    </div>
  );
}

/* ── Priority / Category helpers ── */
const PRIORITY_STYLES = {
  haute:   { border: 'border-red-500',    badge: 'bg-red-100 text-red-700',    icon: '🔴', label: 'Priorité haute' },
  moyenne: { border: 'border-orange-400', badge: 'bg-orange-100 text-orange-700', icon: '🟡', label: 'Priorité moyenne' },
  faible:  { border: 'border-navy-300',   badge: 'bg-navy-100 text-navy-700',   icon: '🔵', label: 'Priorité faible' },
};
const CAT_STYLES = {
  finance:     'bg-navy-100 text-navy-700',
  occupation:  'bg-navy-100 text-navy-700',
  rh:          'bg-green-100 text-green-700',
  facturation: 'bg-red-100 text-red-700',
  commercial:  'bg-amber-100 text-amber-700',
};

/* ── Suggestion Card ── */
function SuggestionCard({ s }) {
  const [open, setOpen] = useState(false);
  const p = PRIORITY_STYLES[s.priorite] || PRIORITY_STYLES.faible;
  return (
    <div className={`bg-white rounded-xl border-l-4 ${p.border} shadow-sm p-5`}>
      <div className="flex flex-wrap gap-2 mb-3">
        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${CAT_STYLES[s.categorie] || 'bg-gray-100 text-gray-600'}`}>
          {s.categorie}
        </span>
        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${p.badge}`}>
          {p.icon} {p.label}
        </span>
        {s.gain_potentiel > 0 && (
          <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-green-100 text-green-700 ml-auto">
            +{formatMontant(s.gain_potentiel)}
          </span>
        )}
      </div>
      <div className="font-bold text-gray-800 text-base mb-1">{s.titre}</div>
      <div className="text-sm text-gray-600 mb-3">{s.description}</div>
      {s.impact && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-800 mb-3">
          💰 Impact : {s.impact}
        </div>
      )}
      {s.action && (
        <button
          onClick={() => setOpen(o => !o)}
          className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium"
        >
          <ChevronRight size={15} className={`transition-transform ${open ? 'rotate-90' : ''}`} />
          Voir l'action
        </button>
      )}
      {open && s.action && (
        <div className="mt-2 bg-gray-50 rounded-lg px-3 py-2 text-sm text-gray-700 border">
          {s.action}
        </div>
      )}
    </div>
  );
}

/* ── Statut badge ── */
function StatutBadge({ statut }) {
  const map = {
    en_attente: ['bg-orange-100 text-orange-700', 'En attente'],
    accepte:    ['bg-green-100 text-green-700',   'Accepté'],
    refuse:     ['bg-red-100 text-red-700',        'Refusé'],
    expire:     ['bg-gray-100 text-gray-600',      'Expiré'],
    retard:     ['bg-red-100 text-red-700',        'En retard'],
    paye:       ['bg-green-100 text-green-700',    'Payé'],
    envoye:     ['bg-navy-100 text-navy-700',      'Envoyé'],
  };
  const [cls, label] = map[statut] || ['bg-gray-100 text-gray-600', statut];
  return <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${cls}`}>{label}</span>;
}

/* ══════════════════════════════════════
   MAIN PAGE
══════════════════════════════════════ */
export default function AnalyticsPage() {
  const [tab, setTab] = useState('global');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [optimLoading, setOptimLoading] = useState(false);
  const [suggestions, setSuggestions] = useState(null);

  const loadData = useCallback(() => {
    setLoading(true);
    setError(null);
    analyticsApi.getSynthese()
      .then(r => setData(r.data))
      .catch(() => setError('Impossible de charger les données analytiques.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const runOptimisations = () => {
    setOptimLoading(true);
    setSuggestions(null);
    analyticsApi.getOptimisations()
      .then(r => setSuggestions(r.data))
      .catch(() => setSuggestions({ erreur: true }))
      .finally(() => setOptimLoading(false));
  };

  /* ── derived ── */
  const kpi = data?.kpi || {};
  const structs = data?.structures || [];
  const ptislu = structs.find(s => s.nom?.toLowerCase().includes('ptis')) || structs[0];
  const choubi  = structs.find(s => s.nom?.toLowerCase().includes('choub')) || structs[1];
  const caData  = data?.ca_mensuel || [];
  const retards = data?.factures_retard || [];
  const heuresSup = data?.heures_sup || [];
  const devis   = data?.devis_en_cours || [];
  const gainTotal = (suggestions?.suggestions || []).reduce((s, x) => s + (x.gain_potentiel || 0), 0);

  const occPtislu  = ptislu?.capacite  > 0 ? Math.round((ptislu.enfants_actifs  / ptislu.capacite)  * 100) : 0;
  const occChoubi  = choubi?.capacite > 0 ? Math.round((choubi.enfants_actifs / choubi.capacite) * 100) : 0;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-primary-500 flex items-center justify-center">
          <TrendingUp size={20} className="text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analyse &amp; Optimisations</h1>
          <p className="text-sm text-gray-500">Tableau de bord analytique — SAS Les Cachettes</p>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-center gap-2 text-red-700">
          <AlertTriangle size={18} /> {error}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 mb-6 w-fit">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              tab === t.id ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ═══ TAB 1: VUE GLOBALE ═══ */}
      {tab === 'global' && (
        <div className="space-y-6">
          {/* KPI bar */}
          <div className="flex gap-4 flex-wrap">
            <KpiCard loading={loading} label="MRR Total" icon={DollarSign} colorClass="text-navy-700"
              value={loading ? null : formatMontant(kpi.mrr_total)}
              sub="Revenu mensuel récurrent" />
            <KpiCard loading={loading} label="Taux d'encaissement" icon={TrendingUp} colorClass="text-green-600"
              value={loading ? null : `${kpi.taux_encaissement?.toFixed(1) ?? '—'}%`}
              sub="Encaissé / Facturé" />
            <KpiCard loading={loading} label="Enfants actifs" icon={Users} colorClass="text-violet-600"
              value={loading ? null : kpi.enfants_actifs_total ?? '—'}
              sub="Toutes structures" />
            <KpiCard loading={loading} label="Revenus devis en cours" icon={FileText} colorClass="text-amber-600"
              value={loading ? null : formatMontant(kpi.revenus_devis_en_cours)}
              sub="Potentiel non signé" />
          </div>

          {/* Structure comparison */}
          <div className="flex gap-4 flex-wrap">
            <StructureCol data={ptislu}  color={C_PTISLU}    loading={loading} />
            <StructureCol data={choubi}  color={C_CHOUBIDOUX} loading={loading} />
          </div>

          {/* CA Chart */}
          <div className="card p-5">
            <div className="font-bold text-gray-800 mb-4">Évolution du chiffre d'affaires</div>
            {loading ? (
              <Skeleton className="h-64 w-full" />
            ) : caData.length === 0 ? (
              <div className="h-48 flex items-center justify-center text-gray-400 text-sm">
                Aucune donnée de CA disponible
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={caData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <defs>
                    <linearGradient id="gPtislu" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={C_PTISLU} stopOpacity={0.25} />
                      <stop offset="95%" stopColor={C_PTISLU} stopOpacity={0.02} />
                    </linearGradient>
                    <linearGradient id="gChoubi" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={C_CHOUBIDOUX} stopOpacity={0.25} />
                      <stop offset="95%" stopColor={C_CHOUBIDOUX} stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="mois" tick={{ fontSize: 12 }} />
                  <YAxis tickFormatter={v => `${v}€`} tick={{ fontSize: 12 }} width={60} />
                  <Tooltip content={<CaTooltip />} />
                  <Legend />
                  <Area type="monotone" dataKey="ptislu"    name="Ptis Lu"    stroke={C_PTISLU}    fill="url(#gPtislu)"  strokeWidth={2} dot={false} />
                  <Area type="monotone" dataKey="choubidoux" name="Choubidoux" stroke={C_CHOUBIDOUX} fill="url(#gChoubi)" strokeWidth={2} dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Occupation gauges */}
          <div className="card p-5">
            <div className="font-bold text-gray-800 mb-4">Taux d'occupation par structure</div>
            {loading ? (
              <div className="flex gap-8 justify-center">
                <Skeleton className="h-40 w-40 rounded-full" />
                <Skeleton className="h-40 w-40 rounded-full" />
              </div>
            ) : (
              <div className="flex gap-12 justify-center flex-wrap py-2">
                {ptislu  && <OccupationGauge nom={ptislu.nom}  pct={occPtislu}  color={C_PTISLU} />}
                {choubi  && <OccupationGauge nom={choubi.nom}  pct={occChoubi}  color={C_CHOUBIDOUX} />}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══ TAB 2: IA ═══ */}
      {tab === 'ia' && (
        <div className="space-y-6">
          <div className="card p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
                <Brain size={24} className="text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-1">Analyse intelligente de votre activité</h2>
                <p className="text-gray-500 text-sm mb-4">
                  Georges analyse vos données en temps réel et identifie les opportunités d'optimisation pour votre crèche.
                </p>
                <button
                  onClick={runOptimisations}
                  disabled={optimLoading}
                  className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-xl font-semibold text-sm hover:opacity-90 disabled:opacity-60 transition-all shadow-md"
                >
                  {optimLoading ? (
                    <><Loader2 size={16} className="animate-spin" /> Georges analyse vos données...</>
                  ) : (
                    <><Zap size={16} /> 🧠 Lancer l'analyse</>
                  )}
                </button>
              </div>
            </div>
          </div>

          {suggestions?.erreur && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 flex items-center gap-2">
              <AlertTriangle size={18} /> L'analyse a échoué. Vérifiez la connexion au serveur.
            </div>
          )}

          {suggestions?.suggestions && (
            <>
              <div className="space-y-3">
                {suggestions.suggestions.length === 0 ? (
                  <div className="card p-8 text-center text-gray-400">
                    <Brain size={32} className="mx-auto mb-2 opacity-40" />
                    <div>Aucune suggestion pour le moment. Votre activité est optimale !</div>
                  </div>
                ) : (
                  suggestions.suggestions.map((s, i) => <SuggestionCard key={i} s={s} />)
                )}
              </div>
              {gainTotal > 0 && (
                <div className="rounded-2xl bg-gradient-to-r from-green-500 to-emerald-600 p-5 text-white shadow-lg">
                  <div className="text-lg font-bold">
                    💡 Gain potentiel total identifié : {formatMontant(gainTotal)}/mois
                  </div>
                  <div className="text-green-100 text-sm mt-1">
                    En appliquant toutes les recommandations ci-dessus.
                  </div>
                </div>
              )}
            </>
          )}

          {!suggestions && !optimLoading && (
            <div className="card p-10 text-center text-gray-400">
              <Brain size={40} className="mx-auto mb-3 opacity-30" />
              <div className="text-base font-medium text-gray-500 mb-1">Analyse non démarrée</div>
              <div className="text-sm">Cliquez sur "Lancer l'analyse" pour obtenir des recommandations personnalisées.</div>
            </div>
          )}
        </div>
      )}

      {/* ═══ TAB 3: DÉTAIL FINANCIER ═══ */}
      {tab === 'detail' && (
        <div className="space-y-6">
          {/* Factures en retard */}
          {loading ? (
            <div className="card p-5"><Skeleton className="h-48 w-full" /></div>
          ) : retards.length > 0 ? (
            <div className="card overflow-hidden">
              <div className="bg-red-600 px-5 py-3 flex items-center gap-2">
                <AlertTriangle size={18} className="text-white" />
                <span className="text-white font-semibold">⚠ Factures en retard ({retards.length})</span>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-red-50">
                  <tr>
                    {['Enfant', 'Structure', 'Montant', 'Statut'].map(h => (
                      <th key={h} className="px-4 py-2.5 text-left text-xs font-semibold text-red-700 uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {retards.map((f, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium">{f.enfant_prenom} {f.enfant_nom}</td>
                      <td className="px-4 py-3 text-gray-600">{f.structure_nom}</td>
                      <td className="px-4 py-3 font-semibold text-red-600">{formatMontant(f.montant)}</td>
                      <td className="px-4 py-3"><StatutBadge statut={f.statut || 'retard'} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="card p-5 flex items-center gap-3 text-green-700 bg-green-50 border border-green-200">
              ✅ Aucune facture en retard — excellent !
            </div>
          )}

          {/* Heures supplémentaires */}
          <div>
            <h3 className="font-bold text-gray-800 mb-3">Heures supplémentaires ce mois</h3>
            {loading ? (
              <div className="flex gap-4"><Skeleton className="h-28 flex-1" /><Skeleton className="h-28 flex-1" /></div>
            ) : heuresSup.length === 0 ? (
              <div className="card p-5 text-gray-400 text-sm text-center">Aucune donnée d'heures supplémentaires</div>
            ) : (
              <div className="flex gap-4 flex-wrap">
                {heuresSup.map((s, i) => (
                  <div key={i} className="card p-5 flex-1 min-w-[200px]">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: i === 0 ? C_PTISLU : C_CHOUBIDOUX }} />
                      <span className="font-semibold text-gray-700 text-sm">{s.structure_nom}</span>
                    </div>
                    <div className="text-2xl font-bold text-gray-900">{s.heures_supp ?? '—'}h</div>
                    <div className="text-xs text-gray-400 mb-2">Total heures supp.</div>
                    <div className="text-base font-semibold text-green-600">{formatMontant(s.montant_supp)}</div>
                    <div className="text-xs text-gray-400">Montant généré</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Devis en cours */}
          <div>
            <h3 className="font-bold text-gray-800 mb-3">Devis en attente de signature</h3>
            {loading ? (
              <div className="card p-5"><Skeleton className="h-40 w-full" /></div>
            ) : devis.length === 0 ? (
              <div className="card p-5 text-gray-400 text-sm text-center">Aucun devis en attente de signature</div>
            ) : (
              <div className="card overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      {['Enfant', 'Structure', 'Heures/sem.', 'Montant mensuel', 'Créé le', 'Statut'].map(h => (
                        <th key={h} className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {devis.map((d, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium">{d.enfant_prenom} {d.enfant_nom}</td>
                        <td className="px-4 py-3 text-gray-600">{d.structure_nom}</td>
                        <td className="px-4 py-3">{d.heures_hebdo}h</td>
                        <td className="px-4 py-3 font-semibold">{formatMontant(d.montant_mensuel)}</td>
                        <td className="px-4 py-3 text-gray-500">
                          {d.date_creation ? new Date(d.date_creation).toLocaleDateString('fr-FR') : '—'}
                        </td>
                        <td className="px-4 py-3"><StatutBadge statut={d.statut} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
